/**
 * 단일 `value` 컬럼 Parquet 를 읽는 구현 — `hyparquet` 을 아는 유일한 자리다.
 *
 * Owns:
 * - 바이트 → `ParquetColumn`, 계약 위반의 오류 문구.
 *
 * Does not own:
 * - 바이트를 어디서 받는가 · 시각의 의미 — 소비자(`modules/inspect`).
 */
import { parquetReadObjects } from "hyparquet";

import { ParquetReadError, type ParquetColumn } from "./public";

const VALUE_COLUMN = "value";

/**
 * 배열 파일 하나를 컬럼으로 읽는다.
 *
 * 값의 종류는 첫 행이 정한다 — 엔진이 한 파일에 한 dtype 만 쓴다. 행이 없으면 빈
 * `number` 컬럼이다(빈 채널은 소견으로 따로 알려 온다).
 */
export async function readParquetColumn(bytes: ArrayBuffer): Promise<ParquetColumn> {
  let rows: Record<string, unknown>[];
  try {
    rows = await parquetReadObjects({ file: bytes, columns: [VALUE_COLUMN] });
  } catch (cause) {
    throw new ParquetReadError("Parquet file could not be read", cause);
  }
  const values = rows.map((row) => row[VALUE_COLUMN]);
  if (values.length === 0) {
    return { kind: "number", rows: [] };
  }
  const first = values[0];
  if (typeof first === "bigint") {
    return { kind: "bigint", rows: expect(values, isBigint, "bigint") };
  }
  if (typeof first === "boolean") {
    return { kind: "boolean", rows: expect(values, isBoolean, "boolean") };
  }
  if (typeof first === "number") {
    return { kind: "number", rows: expect(values, isNumber, "number") };
  }
  if (Array.isArray(first)) {
    return { kind: "numberList", rows: values.map(toNumberRow) };
  }
  throw new ParquetReadError(`Parquet column "${VALUE_COLUMN}" holds an unsupported value`);
}

function isBigint(value: unknown): value is bigint {
  return typeof value === "bigint";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number";
}

/** 한 파일 안에서 종류가 섞이면 값을 지어내지 않고 멈춘다. */
function expect<T>(
  values: readonly unknown[],
  guard: (value: unknown) => value is T,
  kind: string,
): T[] {
  return values.map((value) => {
    if (!guard(value)) {
      throw new ParquetReadError(
        `Parquet column "${VALUE_COLUMN}" mixes ${kind} with ${typeof value}`,
      );
    }
    return value;
  });
}

function toNumberRow(value: unknown): number[] {
  if (!Array.isArray(value)) {
    throw new ParquetReadError(
      `Parquet column "${VALUE_COLUMN}" mixes a list with ${typeof value}`,
    );
  }
  return value.map((item) => {
    if (typeof item === "number") {
      return item;
    }
    if (typeof item === "bigint") {
      return Number(item);
    }
    throw new ParquetReadError(`Parquet column "${VALUE_COLUMN}" holds a non-numeric list item`);
  });
}
